import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProcessing } from '../../context/ProcessingContext';
import { renderMarkdown } from '../../lib/markdoc';
import { supabase } from '../../lib/supabase';
import { Alert } from '../../components/Alert';
import { getFeatureCreditCostCache, setFeatureCreditCostCache } from '../../lib/cache';
import { FeaturePageFavoriteButton } from '../../components/FeaturePageFavoriteButton';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft } from 'lucide-react';
import { useBlocker } from '../../hooks/useBlocker';

const baseRentSensSchema = z.object({
  term: z.string()
    .trim()
    .refine((val) => !/^-/.test(val), "Term must be a positive value")
    .refine((val) => /^\d+$/.test(val), "Term must be an integer")
    .transform((val) => parseInt(val))
    .pipe(z.number()
      .min(1, "Term must be at least 1 year")
      .max(15, "Term must be at most 15 years")
    ),
  ti_allow: z.string()
    .trim()
    .transform((val) => val.replace(/,/g, ''))
    .refine((val) => !/^-/.test(val), "TI Allowance must be a positive value")
    .refine((val) => /^\d+(\.\d{0,2})?$/.test(val), "TI Allowance must be a number with at most two decimal places")
    .transform((val) => parseFloat(val))
    .pipe(z.number().positive("TI Allowance must be greater than 0")),
  space_size: z.string()
    .trim()
    .transform((val) => val.replace(/,/g, ''))
    .refine((val) => !/^-/.test(val), "Space Size must be a positive value")
    .refine((val) => /^\d+$/.test(val), "Space Size must be an integer")
    .transform((val) => parseInt(val))
    .pipe(z.number().positive("Space Size must be greater than 0")),
  base_rent_min: z.string()
    .trim()
    .transform((val) => val.replace(/,/g, ''))
    .refine((val) => !/^-/.test(val), "Base Rent Min must be a positive value")
    .refine((val) => /^\d+(\.\d{0,2})?$/.test(val), "Base Rent must be a number with at most two decimal places")
    .transform((val) => parseFloat(val))
    .pipe(z.number().positive("Base Rent must be greater than 0")),
  base_rent_max: z.string()
    .trim()
    .transform((val) => val.replace(/,/g, ''))
    .refine((val) => !/^-/.test(val), "Base Rent Max must be a positive value")
    .refine((val) => /^\d+(\.\d{0,2})?$/.test(val), "Base Rent must be a number with at most two decimal places")
    .transform((val) => parseFloat(val))
    .pipe(z.number().positive("Base Rent must be greater than 0")),
  op_ex_pct: z.string()
    .trim()
    .refine((val) => !/^-/.test(val), "Initial OpEx must be a positive value")
    .refine((val) => /^\d+(\.\d{0,2})?$/.test(val), "Initial OpEx must be a number with at most two decimal places")
    .transform((val) => parseFloat(val))
    .pipe(z.number().positive("Initial OpEx must be greater than 0").max(100, "Initial OpEx must be less than or equal to 100")),
  op_ex_escal: z.string()
    .trim()
    .refine((val) => !/^-/.test(val), "OpEx Escalation Rate must be a positive value")
    .refine((val) => /^\d+(\.\d{0,2})?$/.test(val), "OpEx Escalation Rate must be a number with at most two decimal places")
    .transform((val) => parseFloat(val))
    .pipe(z.number().positive("OpEx Escalation Rate must be greater than 0").max(100, "OpEx Escalation Rate must be less than or equal to 100")),
  ti_rate: z.string()
    .trim()
    .refine((val) => !/^-/.test(val), "TI Amortization Rate must be a positive value")
    .refine((val) => /^\d+(\.\d{0,2})?$/.test(val), "TI Amortization Rate must be a number with at most two decimal places")
    .transform((val) => parseFloat(val))
    .pipe(z.number().positive("TI Amortization Rate must be greater than 0").max(100, "TI Amortization Rate must be less than or equal to 100")),
}).refine((data) => {
  const min = data.base_rent_min;
  const max = data.base_rent_max;
  return min <= max;
}, {
  message: "Base Rent Min must be less than or equal to Base Rent Max",
  path: ["base_rent_min"]
}).refine((data) => {
  const min = data.base_rent_min;
  const max = data.base_rent_max;
  return max <= min * 1.5;
}, {
  message: "Base Rent Max must be no more than 1.5 times Base Rent Min",
  path: ["base_rent_max"]
});

type BaseRentSensFormData = z.infer<typeof baseRentSensSchema>;

export function BaseRentSensitivity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { blockNavigation } = useBlocker();
  const [creditCost, setCreditCost] = useState<number>(5);
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BaseRentSensFormData>({
    resolver: zodResolver(baseRentSensSchema),
    defaultValues: {
      term: "5",
      ti_allow: "35.00",
      space_size: "10,000",
      base_rent_min: "50.00",
      base_rent_max: "65.00",
      op_ex_pct: "15.00",
      op_ex_escal: "3.00",
      ti_rate: "8.00",
    },
  });

  const {
    isProcessing,
    setIsProcessing,
    setProcessingMessage,
    error,
    setError,
    resetProcessingState
  } = useProcessing();

  useEffect(() => {
    if (!user) return;

    const fetchFeatureData = async () => {
      try {
        const { data: featureData, error: featureError } = await supabase
          .from('features')
          .select('id')
          .eq('slug', 'base-rent-sens')
          .single();

        if (featureError) throw featureError;
        setFeatureId(featureData.id);

        const { data: favoriteData, error: favoriteError } = await supabase
          .from('user_favorites')
          .select('feature_id')
          .eq('user_id', user.id)
          .eq('feature_id', featureData.id)
          .maybeSingle();

        if (favoriteError) throw favoriteError;
        setIsFavorited(!!favoriteData);
      } catch (error) {
        console.error('Error fetching feature data:', error);
      }
    };

    fetchFeatureData();
  }, [user]);

  useEffect(() => {
    const fetchCreditCost = async () => {
      const cachedCreditCost = getFeatureCreditCostCache('base-rent-sens');
      if (cachedCreditCost !== null) {
        setCreditCost(cachedCreditCost);
        return;
      }

      const { data, error } = await supabase
        .from('features')
        .select('credit_cost')
        .eq('slug', 'base-rent-sens')
        .single();

      if (!error && data) {
        setCreditCost(data.credit_cost);
        setFeatureCreditCostCache('base-rent-sens', data.credit_cost);
      }
    };

    fetchCreditCost();
  }, []);

  const onSubmit = async (data: BaseRentSensFormData) => {
    if (!user) return;

    try {
      resetProcessingState();
      setIsProcessing(true);
      setProcessingMessage('Generating base rent analysis...');

      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session) {
        throw new Error('Authentication error');
      }

      const text = JSON.stringify(data);

      const workflowUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-workflow-text`;
      const workflowResponse = await fetch(workflowUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Function-Slug': 'base-rent-sens',
        },
        body: JSON.stringify({ text }),
      });

      if (!workflowResponse.ok) {
        throw new Error('Failed to start workflow');
      }

      const reader = workflowResponse.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let outputText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const event = JSON.parse(line.slice(6));
            if (event.event === 'workflow_finished') {
              outputText = event.data?.outputs?.text;
              setResult(outputText + "\n\n---\n\nThe report is also available under **Home → My Reports**. If you encounter any issues accessing it, please contact us at support@axoncre.com.");
            }
          }
        }
      }

      setProcessingMessage('Base rent analysis generated.');
    } catch (error) {
      console.error('Submission failed:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
      setProcessingMessage(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link
        to="/dashboard"
        className="inline-flex items-center text-[--text-secondary] hover:text-[--text-primary] mb-8"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Link>

      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-[--text-primary]">
            Base Rent Analysis
          </h1>
          {featureId && (
            <FeaturePageFavoriteButton
              featureId={featureId}
              initialIsFavorited={isFavorited}
            />
          )}
        </div>
        <div className="space-y-2">
          <p className="mt-2 text-sm sm:text-base text-[--text-secondary]">
            Analyze base rent scenarios to assess lease affordability.
          </p>
          <div className="inline-flex items-center px-3 py-1 rounded-md bg-primary-50 dark:bg-primary-900/30">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {creditCost} credits to run this feature
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[--bg-primary] rounded-lg shadow p-4 sm:p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="term" className="block text-sm font-medium text-[--text-primary]">Lease Term (years)</label>
            <input
              type="text"
              id="term"
              {...register("term")}
              className="pl-2 py-1 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
            />
            {errors.term && <p className="mt-2 text-sm text-error-600">{errors.term.message}</p>}
          </div>

          <div>
            <label htmlFor="ti_allow" className="block text-sm font-medium text-[--text-primary]">TI Allowance ($/sf)</label>
            <input
              type="text"
              id="ti_allow"
              {...register("ti_allow")}
              className="pl-2 py-1 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
            />
            {errors.ti_allow && <p className="mt-2 text-sm text-error-600">{errors.ti_allow.message}</p>}
          </div>

          <div>
            <label htmlFor="space_size" className="block text-sm font-medium text-[--text-primary]">Rentable Square Footage RSF (sf)</label>
            <input
              type="text"
              id="space_size"
              {...register("space_size")}
              className="pl-2 py-1 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
            />
            {errors.space_size && <p className="mt-2 text-sm text-error-600">{errors.space_size.message}</p>}
          </div>

          <div>
            <label htmlFor="ti_rate" className="block text-sm font-medium text-[--text-primary]">TI Amortization Rate (% per year)</label>
            <input
              type="text"
              id="ti_rate"
              {...register("ti_rate")}
              className="pl-2 py-1 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
            />
            {errors.ti_rate && <p className="mt-2 text-sm text-error-600">{errors.ti_rate.message}</p>}
          </div>

          <div>
            <label htmlFor="op_ex_pct" className="block text-sm font-medium text-[--text-primary]">Operating Expense OpEx (% of Base Rent)</label>
            <input
              type="text"
              id="op_ex_pct"
              {...register("op_ex_pct")}
              className="pl-2 py-1 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
            />
            {errors.op_ex_pct && <p className="mt-2 text-sm text-error-600">{errors.op_ex_pct.message}</p>}
          </div>

          <div>
            <label htmlFor="op_ex_escal" className="block text-sm font-medium text-[--text-primary]">Operating Expense Increase (% per year)</label>
            <input
              type="text"
              id="op_ex_escal"
              {...register("op_ex_escal")}
              className="pl-2 py-1 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
            />
            {errors.op_ex_escal && <p className="mt-2 text-sm text-error-600">{errors.op_ex_escal.message}</p>}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="base_rent_min" className="block text-sm font-medium text-[--text-primary]">Base Rent Range ($/sf/yr)</label>
            <div className="flex space-x-2">
              <input
                type="text"
                id="base_rent_min"
                placeholder="Min"
                {...register("base_rent_min")}
                className="pl-2 py-1 mt-1 block w-1/2 border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
              />
              <input
                type="text"
                id="base_rent_max"
                placeholder="Max"
                {...register("base_rent_max")}
                className="pl-2 py-1 mt-1 block w-1/2 border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
              />
            </div>
            {errors.base_rent_min && <p className="mt-2 text-sm text-error-600">{errors.base_rent_min.message}</p>}
            {errors.base_rent_max && <p className="mt-2 text-sm text-error-600">{errors.base_rent_max.message}</p>}
          </div>

          <div className="md:col-span-2">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Submit
            </button>
          </div>
        </form>

      </div>

      {result && (
        <div className="mt-8 bg-[--bg-primary] rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-medium text-[--text-primary] mb-4">
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              {renderMarkdown(result)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}